import { useSyncExternalStore, useCallback } from 'react';
import type { Flow } from '../../contract/flow';
import { skipToken, type SkipToken } from '../skipToken';
import { useRenderSubscription, useRenderSubscriptionCleanup } from './useRenderSubscription';
import { createDeferredNotify } from './createDeferredNotify';

/**
 * Таймаут по умолчанию для временной подписки, удерживающей подписку на поток
 * между render-фазой и mount-фазой компонента.
 *
 * Это страховка для abandoned renders в concurrent mode: если компонент был приостановлен
 * через Suspense и так и не смонтировался, temp-подписка освободится по таймауту.
 */
const DEFAULT_RENDER_SUBSCRIPTION_TIMEOUT_MS = 60_000;

/**
 * Опции для хука {@link useFlow}.
 */
export interface UseFlowOptions {
    /**
     * Таймаут в миллисекундах для временной подписки, которая создается на время
     * между render и mount компонента (актуально для Suspense сценариев).
     * Для общей подписки нескольких компонентов используется самый поздний deadline:
     * меньший timeout другого компонента не сокращает уже запрошенное время удержания.
     * Значение `0` отключает создание временной подписки.
     * @default 60000
     */
    keepRenderSubscriptionFor?: number;
}

/**
 * Отклоняет `AsyncFlow`: у него есть `asPromise`, а асинхронный поток читается через
 * {@link useAsyncFlow}. Обобщенный `Flow<T>` поля `asPromise` не объявляет и проходит.
 */
interface NotAsyncFlow {
    asPromise?: never;
}

/**
 * @internal Реализация подписки на Flow. Публичный вход — {@link useFlow}, который не принимает
 * AsyncFlow; {@link useAsyncFlow} читает сырое состояние через этот хук напрямую.
 */
export function useFlowInternal<T>(flow: Flow<T> | SkipToken, options?: UseFlowOptions): T | null {
    const subscribe = useCallback(
        (notify: () => void) => {
            if (flow === skipToken) {
                return () => {
                    // ничего не делаем
                };
            }

            const [deferredNotify, cancelPending] = createDeferredNotify(notify);
            const subscription = flow.subscribe(deferredNotify);

            return () => {
                cancelPending();
                subscription.unsubscribe();
            };
        },
        [flow]
    );

    const getSnapshot = useCallback(() => {
        if (flow === skipToken) return null;
        return flow.getSnapshot();
    }, [flow]);

    useRenderSubscription(flow, options?.keepRenderSubscriptionFor ?? DEFAULT_RENDER_SUBSCRIPTION_TIMEOUT_MS);

    const result = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

    useRenderSubscriptionCleanup(flow);

    return result;
}

/**
 * Подписывается на Flow и возвращает его текущее значение.
 *
 * Тип значения не ограничен. Асинхронный поток читается через {@link useAsyncFlow}, `useFlow`
 * отклоняет его на уровне типов.
 *
 * При SSR, гидрации и клиентском рендере хук читает живое значение потока через getSnapshot.
 * Хук не сериализует и не восстанавливает состояние. Паритет SSR обеспечивает поставщик
 * состояния: перед hydrateRoot он должен подготовить данные, соответствующие серверной разметке.
 *
 * Уведомления от Flow доставляются React отложенно, в микротаске. Несколько синхронных
 * уведомлений схлопываются в одно обновление с последним snapshot. Поэтому `flushSync(() =>
 * flow.emit(...))` не делает ререндер синхронным: он произойдет после выхода из `flushSync`.
 * Синхронный режим намеренно не предоставляется, так как отложенная доставка защищает от
 * обновления React-компонента во время рендера другого компонента. Для синхронной реакции вне
 * React следует использовать `flow.subscribe()` напрямую.
 *
 * @typeParam T - Тип значения, хранящегося в Flow
 * @param flow - Экземпляр Flow для подписки, или `skipToken` для пропуска подписки
 * @param options - Параметры подписки на поток
 * @returns Текущее значение из Flow, или `null` если передан `skipToken`
 *
 * @example
 * ```tsx
 * const counterFlow = createFlow(0);
 *
 * function Counter() {
 *   const count = useFlow(counterFlow);
 *   return <div>Count: {count}</div>;
 * }
 * ```
 *
 * @example
 * // Условная подписка с использованием skipToken
 * ```tsx
 * function ConditionalCounter({ enabled }: { enabled: boolean }) {
 *   const count = useFlow(enabled ? counterFlow : skipToken);
 *   return <div>Count: {count ?? 'disabled'}</div>;
 * }
 * ```
 */
export function useFlow(flow: SkipToken, options?: UseFlowOptions): null;
export function useFlow<T>(flow: Flow<T> & NotAsyncFlow, options?: UseFlowOptions): T;
export function useFlow<T>(flow: (Flow<T> & NotAsyncFlow) | SkipToken, options?: UseFlowOptions): T | null;
export function useFlow<T>(flow: (Flow<T> & NotAsyncFlow) | SkipToken, options?: UseFlowOptions): T | null {
    return useFlowInternal(flow, options);
}
